import { useState } from "react";

const input = "w-full rounded-md border border-border px-3 py-2 text-sm outline-none transition-[color,box-shadow] focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30 text-foreground placeholder:text-muted-foreground";
const label = "mb-1 block text-xs font-medium text-muted-foreground";

const FIXTURE_RANK = { global: 0, competition: 1, match: 2 };

/**
 * The football module's own step, shown inside PlacementPicker once that cluster is
 * ticked and a question type is chosen. Built to the same shape as Ke2027Detail beside
 * it: the scope a question type needs (deepest of the chosen ones — naming a fixture
 * already names its competition), the competition or the two sides that fixes, and
 * inline "+ Add a club" since not every club in every division is in the seed.
 */
export default function FootballDetail({
  kinds,
  tagIds = [],
  competitionId,
  homeTeamId,
  awayTeamId,
  competitions = [],
  teams = [],
  onChange,
  onCreateTeam,
  errors = {},
}) {
  const [newTeam, setNewTeam] = useState({ name: "", code: "", competitionId: "" });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const chosenKinds = (kinds || []).filter((k) => tagIds.includes(k.id));

  let need = { level: "global", optional: true };
  for (const k of chosenKinds) {
    if (!k.level || !(k.level in FIXTURE_RANK)) continue;
    if (FIXTURE_RANK[k.level] > FIXTURE_RANK[need.level]) need.level = k.level;
    if (!k.unitOptional && k.level !== "global") need.optional = false;
  }
  if (need.level === "global") need.optional = true;

  const offHub = chosenKinds.filter((k) => k.onHub === false);

  if (!chosenKinds.length) {
    return <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Pick what this settles on above, and this will ask which competition or fixture it concerns.</p>;
  }

  async function addTeam(e) {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      const res = await onCreateTeam({
        name: newTeam.name.trim(),
        code: newTeam.code.trim() || undefined,
        competitionId: newTeam.competitionId || null,
      });
      // Offered as the home side by default — whichever field is empty is the more
      // useful place for a club somebody just typed to land.
      if (!homeTeamId) onChange({ homeTeamId: res.team.id });
      else if (!awayTeamId) onChange({ awayTeamId: res.team.id });
      setNewTeam({ name: "", code: "", competitionId: "" });
      setShowAdd(false);
    } catch (err) {
      setAddError(err.message || "Could not add that club.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="mt-4 space-y-4 rounded-xl bg-gray-50 p-3 dark:bg-navy-900/40">
      {need.level !== "global" && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Which competition?</p>
          {need.optional && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Leave it blank if this isn't tied to one competition.</p>}
          <div className="mt-3">
            <label className={label}>Competition{!need.optional && <span className="text-destructive"> *</span>}</label>
            <select
              className={input}
              value={competitionId || ""}
              onChange={(e) => onChange({ competitionId: e.target.value || null, homeTeamId: null, awayTeamId: null })}
            >
              <option value="">{need.optional ? "Not tied to one" : "Choose a competition"}</option>
              {competitions.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.country})</option>
              ))}
            </select>
            {errors.competitionId && <p className="mt-1 text-xs font-medium text-red-500">{errors.competitionId}</p>}
          </div>

          {need.level === "match" && (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className={label}>Home<span className="text-destructive"> *</span></label>
                <select className={input} value={homeTeamId || ""} disabled={!competitionId} onChange={(e) => onChange({ homeTeamId: e.target.value || null })}>
                  <option value="">{competitionId ? "Choose a club" : "Choose a competition first"}</option>
                  {teams.filter((t) => t.id !== awayTeamId).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {errors.homeTeamId && <p className="mt-1 text-xs font-medium text-red-500">{errors.homeTeamId}</p>}
              </div>
              <div>
                <label className={label}>Away<span className="text-destructive"> *</span></label>
                <select className={input} value={awayTeamId || ""} disabled={!competitionId} onChange={(e) => onChange({ awayTeamId: e.target.value || null })}>
                  <option value="">{competitionId ? "Choose a club" : "Choose a competition first"}</option>
                  {teams.filter((t) => t.id !== homeTeamId).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {errors.awayTeamId && <p className="mt-1 text-xs font-medium text-red-500">{errors.awayTeamId}</p>}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-muted-foreground">Club not listed?</p>
        <button type="button" onClick={() => setShowAdd((v) => !v)} className="mt-2 rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-white/20 dark:text-gray-300 dark:hover:bg-white/5">
          + Add a club
        </button>

        {showAdd && (
          <form onSubmit={addTeam} className="mt-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className={label}>Club name</label>
                <input className={input} required value={newTeam.name} placeholder="e.g. Ushuru FC" onChange={(e) => setNewTeam((t) => ({ ...t, name: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Code</label>
                <input className={input} value={newTeam.code} placeholder="USH" onChange={(e) => setNewTeam((t) => ({ ...t, code: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Competition</label>
                <select className={input} value={newTeam.competitionId} onChange={(e) => setNewTeam((t) => ({ ...t, competitionId: e.target.value }))}>
                  <option value="">Unlisted</option>
                  {competitions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {addError && <p className="mt-2 text-xs font-medium text-red-500">{addError}</p>}
            <div className="mt-3 flex gap-2">
              <button type="submit" disabled={adding || newTeam.name.trim().length < 2} className="rounded-sm bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
                {adding ? "Adding…" : "Add club"}
              </button>
              <button type="button" onClick={() => { setShowAdd(false); setAddError(null); }} className="rounded-sm border border-gray-200 px-4 py-2 text-xs font-semibold dark:border-white/10 dark:text-white">
                Cancel
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">Added clubs are available to everyone creating a football market.</p>
          </form>
        )}
      </div>

      {offHub.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The hub has tabs for fixtures, the title race, continental qualification, relegation and top scorer.{" "}
          {offHub.map((k) => k.name || k.id).join(", ")}{offHub.length === 1 ? " does not have one yet" : " do not have one yet"} — the market is still created and still reachable, it just has no dedicated tab.
        </p>
      )}
    </div>
  );
}
